/*
 * Build your bag: <build-your-bag>. Data comes from #byb-data (sections/build-your-bag.liquid); cart calls go through
 * window.bybAdapter (assets/byb-adapter.js). Initials never reach the URL, analytics or the console.
 * Debug: ?byb_debug=1, plus &byb_test_layers=1 for the _test layers and &byb_test_404=1 to break the charm layer.
 */
(() => {
const params = new URLSearchParams(location.search);
const DEBUG = params.get('byb_debug') === '1';
const TEST = DEBUG && params.get('byb_test_layers') === '1';
const TEST_404 = TEST && params.get('byb_test_404') === '1';
const EASE = 'cubic-bezier(0.23, 1, 0.32, 1)';
// extras: links made before the scarf and keyring steps were split, cleared when the URL is rewritten
const STATE_KEYS = ['style', 'colour', 'variant', 'strap', 'scarf', 'keyring', 'extras', 'step'];
// Each accessory step and the list (role) it shows. The role is the _byb_role line property and the test layer key.
const ACC_STEPS = { strap: 'strap', scarf: 'scarf', keyring: 'charm' };
const ROLES = Object.values(ACC_STEPS);
const WIDTHS = [400, 800, 1200, 1600];
const STAGE_SIZES = '(min-width: 1000px) 55vw, 100vw';
// The stage gallery: at most this many photos of the shown bag; on phones and tablets these steps show photo 1 only
const GALLERY_MAX = 6;
const STRIP_STEPS = new Set(['strap', 'scarf', 'keyring', 'initials', 'review']);
const stripMedia = matchMedia('(max-width: 999px)');
const TILE_SIZES = '(min-width: 1200px) 9vw, (min-width: 1000px) 12vw, (min-width: 700px) 30vw, 40vw';
const FONT_CLASS = { Classic: 'classic', Script: 'script', 'Classic XL': 'xl' };
const reduced = matchMedia('(prefers-reduced-motion: reduce)');

const log = (...args) => DEBUG && console.info('[byb]', ...args);
const esc = (value) => String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
// Placeholders arrive from the section as __name__ (Shopify's Liquid ends an output tag at the first brace)
const fill = (text, values) => text.replace(/__(\w+)__/g, (match, key) => (key in values ? values[key] : match));
const srcset = (image) => WIDTHS.map((w) => `${image.src[w]} ${w}w`).join(', ');
// The file name of an image URL: the same file is served from cdn.shopify.com by /products/{handle}.js and from the
// shop's own /cdn/ path by Liquid, so the paths differ and only the name matches
const fileName = (url) => { try { return new URL(url, location.href).pathname.split('/').pop(); } catch (error) { return String(url); } };
const publish = (name, data) => {
  try {
    window.Shopify?.analytics?.publish?.(name, data);
  } catch (error) {
    // Analytics must never break the build
  }
};
const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(8)), (b) => 'abcdefghjkmnpqrstuvwxyz23456789'[b % 31]).join('');

class BuildYourBag extends HTMLElement {
  connectedCallback() {
    if (this.D) return;
    const D = (this.D = JSON.parse(this.querySelector('#byb-data').textContent));
    // Shopify's t filter HTML-escapes translations (You&#39;ve); decode once so they are plain text, escaped on output
    const decoder = document.createElement('textarea');
    this.T = Object.fromEntries(Object.entries(D.strings).map(([key, text]) => { decoder.innerHTML = text ?? ''; return [key, decoder.value]; }));
    this.cfg = D.settings;
    const lang = document.documentElement.lang || 'en-GB';
    this.money = new Intl.NumberFormat(lang, { style: 'currency', currency: D.cartCurrency });
    // Offer amounts are in the shop's currency, shown without pence unless there are some (25, but 22.50)
    const offerMoney = (digits) => new Intl.NumberFormat(lang, { style: 'currency', currency: D.shopCurrency, minimumFractionDigits: digits, maximumFractionDigits: digits });
    this.offerMoney = [offerMoney(0), offerMoney(2)];
    // The offer is fixed in the section's data island: percent (off each accessory) or tiers (two or three for a set
    // price); at most bundleQty accessories in one build, never above 3 for tiers, and maxPerStep from one step
    this.mode = this.cfg.offerMode === 'tiers' ? 'tiers' : 'percent';
    const cap = Math.max(0, Number(this.cfg.bundleQty) || 0);
    this.cap = this.mode === 'tiers' ? Math.min(cap, 3) : cap;
    this.perStep = Math.max(0, Number(this.cfg.maxPerStep) || 0);
    const T = this.T;
    this.stepLabels = { style: T.labelStyle, colour: T.labelColour, strap: T.labelStrap, scarf: T.labelScarf, keyring: T.labelKeyring, initials: T.initialsLabel, review: T.review };
    this.roleLabels = { strap: T.labelStrap, scarf: T.labelScarf, charm: T.labelKeyring };

    // An accessory's role is the first list it appears in
    this.roleOf = {};
    this.lists = { strap: [], scarf: [], charm: [] };
    for (const role of ROLES) {
      for (const id of D.lists[role]) {
        if (!D.accessories[id]) continue;
        if (this.roleOf[id]) {
          log(`${D.accessories[id].title} is in the ${this.roleOf[id]} and ${role} lists; using ${this.roleOf[id]}`);
          continue;
        }
        this.roleOf[id] = role;
        this.lists[role].push(id);
      }
    }

    this.styles = D.styles.filter((style) => style.key);
    this.S = { style: null, colour: null, variant: null, acc: { strap: [], scarf: [], charm: [] }, pz: this.emptyPz() };
    this.visited = new Set();
    this.prefetched = new Set();
    this.layerOk = new Map();
    this.stageToken = 0;
    // The stage gallery: photos per bag, fetched once (galleryFor); which bags she has swiped (one event each)
    this.galleryCache = new Map();
    this.swiped = new Set();

    const $ = (selector) => this.querySelector(selector);
    this.els = {
      form: $('[data-form]'), progress: $('[data-progress]'), message: $('[data-message]'), total: $('[data-total]'),
      back: $('[data-back]'), next: $('[data-next]'), look: $('[data-look]'), layers: $('[data-layers]'),
      tiles: $('[data-tiles]'), title: $('[data-title]'), intro: $('[data-intro]'), bundle: $('[data-bundle]'),
      made: $('[data-made]'), galleryCount: $('[data-gallery-count]'), galleryPrev: $('[data-gallery-prev]'), galleryNext: $('[data-gallery-next]')
    };
    this.gallery = { pid: Number(this.els.look.dataset.pid) || null, count: 1, index: 0 };
    // The section's own head, put back on every step but the review
    const { title, intro, bundle } = this.els;
    this.head = { title: title.textContent, intro: intro.textContent, introHidden: intro.hidden, bundle: bundle.textContent, bundleHidden: bundle.hidden };

    this.els.back.addEventListener('click', () => this.go(this.index() - 1));
    this.els.next.addEventListener('click', () => this.onNext());
    this.els.form.addEventListener('submit', (event) => { event.preventDefault(); this.onNext(); });
    this.els.form.addEventListener('change', (event) => this.onChange(event));
    this.els.form.addEventListener('input', (event) => this.onInput(event));
    this.addEventListener('click', (event) => this.onClick(event));
    this.els.look.addEventListener('scroll', () => this.onGalleryScroll(), { passive: true });
    this.els.look.addEventListener('keydown', (event) => this.onGalleryKey(event));
    this.els.galleryPrev.addEventListener('click', () => this.galleryTo(this.gallery.index - 1));
    this.els.galleryNext.addEventListener('click', () => this.galleryTo(this.gallery.index + 1));

    this.restore();
    // A page that opens at the review step (refresh, shared link, back button) has had its review: no event, no burst
    this.reviewSeen = this.step === 'review';
    if (DEBUG) this.dataset.debug = '';
    this.classList.add('is-ready');
    publish('byb_viewed', { style_count: this.styles.length });
    publish('byb_step_viewed', { step: this.index() + 1, step_name: this.step });
    this.render(false);
    this.loadLayers();
  }

  /* ---------- lookups ---------- */

  emptyPz() { return { placement: '', font: '', initials: '', emboss: '' }; }
  product(id) { return this.D.bags[id] || this.D.accessories[id]; }
  chosenStyle() { return this.styles.find((style) => style.key === this.S.style) || null; }
  bag() { return this.D.bags[this.S.colour] || null; }
  firstAvailable(variants) { return variants.find((v) => v.available) || variants[0] || null; }
  variant() {
    const bag = this.bag();
    return bag ? bag.variants.find((v) => v.id === this.S.variant) || this.firstAvailable(bag.variants) : null;
  }
  accVariant(id) { return this.firstAvailable(this.D.accessories[id].variants); }
  accIds() { return ROLES.flatMap((role) => this.S.acc[role]); }
  // How many more one step can take: the lower of the per-step limit and what's left of the total
  room(role) { return Math.max(0, Math.min(this.perStep - this.S.acc[role].length, this.cap - this.accIds().length)); }
  styleColours(style) { return style.colours.filter((id) => this.D.bags[id]); }
  styleAvailable(style) { return this.styleColours(style).some((id) => this.D.bags[id].available); }
  heroFor(style) {
    const colours = this.styleColours(style);
    return this.D.bags[colours.find((id) => this.D.bags[id].available) || colours[0]] || null;
  }
  fromPrice(style) {
    const prices = this.styleColours(style).map((id) => this.D.bags[id]).filter((b) => b.available).map((b) => b.price);
    return prices.length ? Math.min(...prices) : null;
  }
  accepts(style, role) { return Boolean(style && style[role] && this.lists[role].length); }

  steps() {
    const style = this.chosenStyle();
    const bag = this.bag();
    const steps = ['style', 'colour'];
    for (const [step, role] of Object.entries(ACC_STEPS)) if (!style || this.accepts(style, role)) steps.push(step);
    const pz = bag ? bag.pz.on : !style || this.styleColours(style).some((id) => this.D.bags[id].pz.on);
    if (pz) steps.push('initials');
    steps.push('review');
    return steps;
  }

  index() { return Math.max(0, this.steps().indexOf(this.step)); }
  firstIncomplete() {
    if (!this.chosenStyle()) return 'style';
    if (!this.variant()?.available) return 'colour';
    return null;
  }
  canEnter(name) {
    const steps = this.steps();
    const blocker = this.firstIncomplete();
    return steps.includes(name) && (!blocker || steps.indexOf(name) <= steps.indexOf(blocker));
  }
  valid(name) {
    if (name === 'style') return Boolean(this.chosenStyle());
    if (name === 'colour') return Boolean(this.variant()?.available);
    return true;
  }

  /* ---------- prices ---------- */

  accPrice(id) { return this.accVariant(id)?.price ?? this.D.accessories[id].price; }

  // The saving the matching discount gives these accessory list prices (pence), whether or not it is live.
  // Percent: each accessory's own saving rounded half up to the penny (Math.round, prices are positive), then summed.
  // Tiers: two or three for a set price, never more than their list prices.
  offerSaving(prices) {
    const c = this.cfg;
    if (this.mode === 'percent') {
      const percent = Math.min(Math.max(Number(c.percentOff) || 0, 0), 100);
      return prices.reduce((saving, price) => saving + Math.round((price * percent) / 100), 0);
    }
    const offer = Number(prices.length === 2 ? c.tier2 : prices.length === 3 ? c.tier3 : 0) || 0;
    return offer > 0 ? Math.max(0, prices.reduce((sum, price) => sum + price, 0) - offer) : 0;
  }

  totals() {
    const bagPrice = this.variant()?.price || 0;
    const accessories = this.accIds().map((id) => ({ id, price: this.accPrice(id) }));
    const eligible = this.D.cartCurrency === this.D.shopCurrency;
    // Only while the matching discount is live and the cart is in the shop's currency; otherwise list prices
    const saving = this.cfg.discountLive && eligible && this.bag() ? this.offerSaving(accessories.map((a) => a.price)) : 0;
    const total = bagPrice + accessories.reduce((sum, a) => sum + a.price, 0) - saving;
    return { bagPrice, accessories, saving, eligible, total };
  }
  format(minor) { return this.money.format(minor / 100); }
  offerAmount(minor) { return this.offerMoney[minor % 100 ? 1 : 0].format(minor / 100); }

  // The line under each accessory step heading: the offer for the current count while the discount is live, otherwise
  // only the limit (no prices)
  offerLine() {
    const T = this.T;
    const c = this.cfg;
    const n = this.accIds().length;
    const max = this.cap;
    const full = n >= max;
    if (!c.discountLive) return fill(full ? T.capFull : T.capSome, { max });
    if (this.mode === 'percent') return fill(full ? T.offerPercentFull : T.offerPercentSome, { percent: Number(c.percentOff) || 0, max });
    const tier2 = Number(c.tier2) || 0;
    const tier3 = Number(c.tier3) || 0;
    const two = this.offerAmount(tier2);
    const three = this.offerAmount(tier3);
    if (n === 0) return fill(T.offerTiers0, { two, three });
    if (n === 1) return fill(T.offerTiers1, { two });
    if (n === 2) return full ? fill(T.offerTiers2Full, { two }) : fill(T.offerTiers2, { two, gap: this.offerAmount(tier3 - tier2) });
    return fill(T.offerTiers3, { three });
  }

  /* ---------- state from and to the URL ---------- */

  restore() {
    const S = this.S;
    const style = this.styles.find((s) => s.key === params.get('style') && this.styleAvailable(s));
    if (style) {
      S.style = style.key;
      this.prefetch(style);
      const colour = Number(params.get('colour'));
      if (this.styleColours(style).includes(colour) && this.D.bags[colour].available) {
        this.setColour(colour);
        const variant = this.D.bags[colour].variants.find((v) => v.id === Number(params.get('variant')) && v.available);
        if (variant) S.variant = variant.id;
      }
      for (const [step, role] of Object.entries(ACC_STEPS)) {
        if (!this.accepts(style, role)) continue;
        S.acc[role] = (params.get(step) || '').split(',').map(Number)
          .filter((id, i, all) => id && all.indexOf(id) === i && this.roleOf[id] === role && this.accVariant(id)?.available)
          .slice(0, this.room(role));
      }
    }
    const wanted = params.get('step');
    const blocker = this.firstIncomplete();
    this.step = blocker && blocker !== 'colour' ? blocker
      : wanted && this.canEnter(wanted) ? wanted
      : blocker || this.steps()[2];
    this.steps().slice(0, this.index()).forEach((name) => this.visited.add(name));
    this.visited.add(this.step);
  }

  writeUrl() {
    const url = new URL(location.href);
    STATE_KEYS.forEach((key) => url.searchParams.delete(key));
    const S = this.S;
    const set = (key, value) => value && url.searchParams.set(key, value);
    set('style', S.style);
    set('colour', S.colour);
    if (this.bag()?.variants.length > 1) set('variant', S.variant);
    for (const [step, role] of Object.entries(ACC_STEPS)) set(step, S.acc[role].join(','));
    set('step', this.step);
    history.replaceState(history.state, '', url);
  }

  /* ---------- changes ---------- */

  setStyle(key) {
    const S = this.S;
    if (key === S.style) return;
    const before = this.accIds().length;
    S.style = key;
    S.colour = null;
    S.variant = null;
    const style = this.chosenStyle();
    for (const role of ROLES) if (!this.accepts(style, role)) S.acc[role] = [];
    if (this.accIds().length < before) this.say(this.T.styleChange);
    this.prefetch(style);
  }

  setColour(id) {
    const S = this.S;
    S.colour = id;
    const bag = this.bag();
    S.variant = this.firstAvailable(bag.variants)?.id || null;
    // Keep only personalisation choices this product offers, as its product page would
    const P = S.pz;
    const pz = bag.pz;
    if (!pz.on) { S.pz = this.emptyPz(); return; }
    if (P.placement && !(pz.bag && pz.tag)) P.placement = '';
    if (P.font === 'Classic XL' && !pz.xl) P.font = '';
    if (P.emboss === 'Gold' && !pz.gold) P.emboss = 'Blind';
    if (!pz.gold && P.initials) P.emboss = 'Blind';
    P.initials = P.initials.slice(0, this.maxInitials());
  }

  maxInitials() { return this.S.pz.font === 'Classic XL' ? 2 : 6; }

  onChange(event) {
    const input = event.target;
    const { name, value } = input;
    const S = this.S;
    const id = Number(value);
    let selected = null;

    if (name === 'style') this.setStyle(value);
    else if (name === 'colour') { this.setColour(id); selected = id; }
    else if (name === 'variant') S.variant = id;
    else if (Object.hasOwn(ACC_STEPS, name)) {
      const role = ACC_STEPS[name];
      if (!input.checked) {
        S.acc[role] = S.acc[role].filter((x) => x !== id);
        this.say('');
      } else if (!S.acc[role].includes(id) && this.room(role) > 0) {
        S.acc[role] = [...S.acc[role], id];
        selected = id;
        // At the limit the rest of the tiles are disabled; say why
        if (!this.room(role)) this.say(this.offerLine());
      }
    } else if (['placement', 'font', 'emboss'].includes(name)) {
      S.pz[name] = value;
      if (name === 'font') S.pz.initials = S.pz.initials.slice(0, this.maxInitials());
    } else return;

    if (name === 'style') publish('byb_option_selected', { step_name: 'style', product_id: null, price: null, currency: this.D.cartCurrency });
    if (selected) {
      const product = this.product(selected);
      publish('byb_option_selected', { step_name: this.step, product_id: selected, price: product.price, currency: this.D.cartCurrency });
    }
    this.render(false);
  }

  onInput(event) {
    if (event.target.name !== 'initials') return;
    const input = event.target;
    const P = this.S.pz;
    const bag = this.bag();
    const raw = input.value;
    const clean = raw.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, this.maxInitials());
    this.querySelector('[data-initials-error]').textContent = clean !== raw.toUpperCase() ? this.T.initialsInvalid : '';
    if (input.value !== clean) input.value = clean;
    P.initials = clean;

    // The product page leaves font and embossing unchosen; here the first option is chosen for her once she types,
    // visibly, so a build never goes to the cart with initials and no font
    if (clean && bag) {
      const choose = (name, value) => {
        if (P[name] || !value) return;
        P[name] = value;
        const radio = this.querySelector(`input[name="${name}"][value="${value}"]`);
        if (radio) radio.checked = true;
      };
      if (bag.pz.bag && bag.pz.tag) choose('placement', 'Bag');
      choose('font', 'Classic');
      choose('emboss', bag.pz.gold ? 'Gold' : 'Blind');
    }
    const slot = this.querySelector('[data-pz-slot]');
    if (slot && bag && !slot.firstChild) slot.innerHTML = this.preview(bag, clean);
    this.querySelectorAll('[data-initials-preview]').forEach((el) => { el.textContent = clean || 'ABC'; });
    this.renderStage();
  }

  onClick(event) {
    const target = event.target.closest('[data-go], [data-skip], [data-remove], [data-share]');
    if (!target || !this.contains(target)) return;
    const S = this.S;

    if (target.hasAttribute('data-go')) return this.go(this.steps().indexOf(target.dataset.go));
    if (target.hasAttribute('data-share')) return this.share();
    if (target.hasAttribute('data-remove')) {
      const id = Number(target.dataset.remove);
      for (const role of ROLES) S.acc[role] = S.acc[role].filter((x) => x !== id);
      this.say('');
      return this.render(false);
    }
    if (Object.hasOwn(ACC_STEPS, this.step)) S.acc[ACC_STEPS[this.step]] = [];
    if (this.step === 'initials') S.pz = this.emptyPz();
    this.go(this.nextIndex());
  }

  onNext() {
    if (this.step === 'review') return this.addToCart();
    if (!this.valid(this.step)) return;
    if (this.step === 'initials' && this.S.pz.initials) {
      const bag = this.bag();
      const placement = this.S.pz.placement || (bag.pz.bag ? 'Bag' : bag.pz.tag ? 'Tag' : '');
      publish('byb_initials_added', { placement, font: this.S.pz.font });
    }
    this.go(this.nextIndex());
  }

  // The step after this one, passing over accessory steps once the build has all the accessories it can take
  nextIndex() {
    const steps = this.steps();
    let i = this.index() + 1;
    while (i < steps.length - 1 && Object.hasOwn(ACC_STEPS, steps[i]) && this.accIds().length >= this.cap) i++;
    return i;
  }

  go(i) {
    const steps = this.steps();
    let name = steps[Math.min(Math.max(i, 0), steps.length - 1)];
    if (!this.canEnter(name)) name = this.firstIncomplete();
    this.step = name;
    this.visited.add(name);
    this.say('');
    publish('byb_step_viewed', { step: steps.indexOf(name) + 1, step_name: name });
    this.render(true);
    if (name === 'review' && !this.reviewSeen) this.reviewReached();
  }

  // The first time the review step opens in a page load: the event, then the celebration. Once only; going back to a
  // step and returning does neither again.
  reviewReached() {
    this.reviewSeen = true;
    publish('byb_review_reached', {
      style: this.S.style, colour: this.bag()?.colour || null, accessory_count: this.accIds().length, has_initials: Boolean(this.S.pz.initials)
    });
    this.celebrate();
  }

  say(text) { this.els.message.textContent = text; }

  /* ---------- rendering ---------- */

  render(focusHeading) {
    const active = document.activeElement;
    const activeId = this.contains(active) ? active.id : null;
    this.dataset.step = this.step;
    this.renderHead();
    this.renderProgress();
    this.els.form.innerHTML = this[`step_${this.step}`]();
    this.renderBar();
    this.renderStage();
    // The compact strip (phones and tablets from the strap step) shows photo 1 only
    if (this.strip()) this.galleryTo(0, true);
    this.syncGallery();
    this.writeUrl();
    // On the swipeable style row, bring the chosen style into view (only the row scrolls, not the page)
    const chosen = this.querySelector('.byb__grid2--styles input:checked')?.closest('.byb__opt');
    if (chosen) chosen.parentElement.scrollLeft += chosen.getBoundingClientRect().left - chosen.parentElement.getBoundingClientRect().left - 16;
    if (focusHeading) {
      // The review step's heading is the page heading above the photo ("Your bag is ready"); the others are step legends
      const review = this.step === 'review';
      const heading = review ? this.els.title : this.querySelector('[data-heading]');
      const phone = matchMedia('(max-width: 999px)').matches;
      heading?.focus({ preventScroll: true });
      // Phones: the review starts at its heading so the intro and offer line are in view; every other step starts at the
      // step indicator, just under the sticky photo (scroll-margin), so the step gets the screen
      if (review) heading.scrollIntoView({ block: phone ? 'start' : 'nearest' });
      else if (heading && phone) this.els.progress.scrollIntoView({ block: 'start' });
      else heading?.scrollIntoView({ block: 'nearest' });
    }
    else if (activeId) document.getElementById(activeId)?.focus();
  }

  // The head above the photo: the section's heading, intro and offer line on steps 1 to 6; on the review step its own,
  // focusable heading (tabindex -1), the build in one line and the offer for the accessories chosen. The made-to-order
  // line under the buttons shows on the review step when the build has initials.
  renderHead() {
    const { title, intro, bundle, made } = this.els;
    const H = this.head;
    const review = this.step === 'review';
    const offer = review ? this.reviewOffer() : '';
    title.textContent = review ? this.T.reviewHeading : H.title;
    if (review) title.tabIndex = -1;
    else title.removeAttribute('tabindex');
    intro.textContent = review ? this.reviewIntro() : H.intro;
    intro.hidden = review ? false : H.introHidden;
    bundle.textContent = review ? offer : H.bundle;
    bundle.hidden = review ? !offer : H.bundleHidden;
    made.textContent = this.T.madeIn;
    made.hidden = !(review && this.S.pz.initials);
  }

  reviewIntro() {
    const T = this.T;
    const P = this.S.pz;
    const values = { style: this.chosenStyle()?.name || '', colour: this.bag()?.colour || '', initials: P.initials };
    if (!P.initials) return fill(T.reviewIntro, values);
    return fill(P.emboss === 'Blind' ? T.reviewIntroBlind : T.reviewIntroGold, values);
  }

  // The review step's offer line for the number of accessories. Shown when the section's offer line is (the discount is
  // live and the headline is set), and only for a cart in the shop's currency, as the three-accessory line quotes the
  // saving. Amounts without pence unless there are some.
  reviewOffer() {
    const T = this.T;
    const c = this.cfg;
    if (this.head.bundleHidden || this.mode !== 'tiers' || !this.totals().eligible) return '';
    const tier2 = Number(c.tier2) || 0;
    const tier3 = Number(c.tier3) || 0;
    const two = this.offerAmount(tier2);
    const three = this.offerAmount(tier3);
    const n = this.accIds().length;
    if (n === 0) return fill(T.reviewOffer0, { two, three });
    if (n === 1) return fill(T.reviewOffer1, { two });
    if (n === 2) return fill(T.reviewOffer2, { two, gap: this.offerAmount(tier3 - tier2) });
    return fill(T.reviewOffer3, { three, saving: this.offerAmount(this.totals().saving) });
  }

  renderProgress() {
    const steps = this.steps();
    const current = this.index();
    const buttons = steps.map((name, i) => {
      const open = i !== current && this.canEnter(name) && (i < current || this.visited.has(name));
      return `<li><button type="button" class="byb__dot" data-go="${name}" aria-label="${esc(this.stepLabels[name])}"${i === current ? ' aria-current="step"' : ''}${open ? '' : ' disabled'}><span></span></button></li>`;
    }).join('');
    this.els.progress.innerHTML = `<p class="byb__count">${esc(fill(this.T.stepCount, { n: current + 1, total: steps.length }))}</p><ol class="byb__dots" role="list">${buttons}</ol>`;
  }

  renderBar() {
    const style = this.chosenStyle();
    const T = this.T;
    let total = '';
    if (this.bag()) total = `${T.total} ${this.format(this.totals().total)}`;
    else if (style && this.fromPrice(style) !== null) total = fill(T.fromPrice, { price: this.format(this.fromPrice(style)) });
    this.els.total.textContent = total;
    this.els.back.hidden = this.index() === 0;
    const review = this.step === 'review';
    const after = Object.hasOwn(ACC_STEPS, this.step) ? this.steps()[this.nextIndex()] : null;
    this.els.next.textContent = review ? T.addToCart : after ? fill(T.nextStep, { step: this.stepLabels[after] }) : T.next;
    this.els.next.disabled = Boolean(this.busy) || !this.valid(this.step);
    this.els.next.toggleAttribute('aria-busy', Boolean(this.busy));
  }

  fieldset(heading, helper, body, skip) {
    return `<fieldset class="byb__step"><legend class="byb__legend"><h2 class="h4" tabindex="-1" data-heading>${esc(heading)}</h2></legend>`
      + (helper ? `<p class="byb__helper" id="byb-help">${helper}</p>` : '')
      + body
      + (skip ? `<button type="button" class="byb__skip" data-skip>${esc(this.T.skip)}</button>` : '')
      + '</fieldset>';
  }

  option({ type = 'radio', name, value, checked, disabled, reason, content, cls = '' }) {
    const id = `byb-${name}-${value}`;
    const describedBy = reason ? ` aria-describedby="${reason}"` : '';
    return `<label class="byb__opt ${cls}${disabled ? ' is-disabled' : ''}" for="${id}"><input class="sr-only" type="${type}" id="${id}" name="${name}" value="${esc(value)}"${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}${describedBy}><span class="byb__tile">${content}</span></label>`;
  }

  img(image, sizes, cls = 'byb__thumb') {
    if (!image) return `<span class="${cls}"></span>`;
    return `<span class="${cls}"><img src="${image.src[400]}" srcset="${srcset(image)}" sizes="${sizes}" width="${image.w}" height="${image.h}" alt="" loading="lazy"></span>`;
  }

  soldOut(id) { return `<span class="byb__so" id="byb-so-${id}">${esc(this.T.soldOut)}</span>`; }

  // A bag that ships later keeps its date ("Ships from 16 November", snippets/jlr-dispatch-date.liquid) on its colour
  // tile and review line, and shows nothing without one. No button label: the build goes straight to the cart, and
  // product.general.pre_order_button reads "Add to bag" anyway.
  preorder(product) {
    if (!product.dispatch) return '';
    return `<span class="byb__meta">${esc(product.dispatch)}</span>`;
  }

  step_style() {
    const options = this.styles.map((style) => {
      const hero = this.heroFor(style);
      const available = this.styleAvailable(style);
      const from = this.fromPrice(style);
      return this.option({
        name: 'style', value: style.key, checked: style.key === this.S.style, disabled: !available,
        reason: available ? '' : `byb-so-${style.key}`, cls: 'byb__opt--style',
        content: this.img(hero?.image, TILE_SIZES)
          + `<span class="byb__name">${esc(style.name)}</span><span class="byb__sub">${esc(style.summary)}</span>`
          + (available && from !== null ? `<span class="byb__price">${esc(fill(this.T.fromPrice, { price: this.format(from) }))}</span>` : this.soldOut(style.key))
      });
    }).join('');
    return this.fieldset(this.T.style, '', `<div class="byb__grid2 byb__grid2--styles">${options}</div>`);
  }

  step_colour() {
    const style = this.chosenStyle();
    const colours = this.styleColours(style).map((id) => {
      const bag = this.D.bags[id];
      return this.option({
        name: 'colour', value: id, checked: id === this.S.colour, disabled: !bag.available,
        reason: bag.available ? '' : `byb-so-${id}`, cls: 'byb__opt--colour',
        content: this.img(bag.image, '64px', 'byb__swatch') + `<span class="byb__name">${esc(bag.colour)}</span>`
          + (bag.available ? this.preorder(bag) : this.soldOut(id))
      });
    }).join('');
    let sizes = '';
    const bag = this.bag();
    if (bag && bag.variants.length > 1) {
      const chips = bag.variants.map((v) => this.option({
        name: 'variant', value: v.id, checked: v.id === this.variant()?.id, disabled: !v.available,
        reason: v.available ? '' : `byb-so-${v.id}`, cls: 'byb__opt--chip',
        content: `${esc(v.title)}${v.available ? '' : ` ${this.soldOut(v.id)}`}`
      })).join('');
      sizes = `<fieldset class="byb__sub-step"><legend class="byb__label">${esc(this.T.size)}</legend><div class="byb__chips">${chips}</div></fieldset>`;
    }
    return this.fieldset(this.T.colour, '', `<div class="byb__grid2">${colours}</div>${sizes}`);
  }

  accessoryOption(id, step) {
    const product = this.D.accessories[id];
    const role = ACC_STEPS[step];
    const chosen = this.S.acc[role].includes(id);
    const available = Boolean(this.accVariant(id)?.available);
    const full = !chosen && !this.room(role);
    return this.option({
      type: 'checkbox', name: step, value: id, checked: chosen, disabled: !available || full,
      reason: !available ? `byb-so-${id}` : full ? 'byb-offer-line' : '', cls: 'byb__opt--acc',
      content: this.img(product.image, TILE_SIZES) + `<span class="byb__name">${esc(product.title)}</span>`
        + `<span class="byb__price">${esc(this.format(product.price))}</span>` + (available ? '' : this.soldOut(id))
    });
  }

  // The slots on the accessory steps, one per accessory a build can take. Filled slots show her choices in step order,
  // each labelled with its type (tap to remove); empty ones are labelled with the steps that have nothing yet, so one of
  // each reads as the suggested path while any mix fits. The current step's slots are marked. Then "1 of 3 added".
  offer() {
    const style = this.chosenStyle();
    const roles = ROLES.filter((role) => this.accepts(style, role));
    const filled = roles.flatMap((role) => this.S.acc[role].map((id) => ({ id, role })));
    const open = roles.filter((role) => !this.S.acc[role].length).map((role) => ({ role }));
    const slots = Array.from({ length: this.cap }, (_, i) => [...filled, ...open][i] || {}).map(({ id, role }) => {
      const cls = `byb__slot${id ? ' is-filled' : ''}${role && role === ACC_STEPS[this.step] ? ' is-current' : ''}`;
      const label = `<span class="byb__slot-label">${role ? esc(this.roleLabels[role]) : ''}</span>`;
      if (!id) return `<li class="${cls}"><span class="byb__slot-box"></span>${label}</li>`;
      const product = this.D.accessories[id];
      return `<li class="${cls}"><button type="button" class="byb__slot-box" data-remove="${id}" aria-label="${esc(`${this.T.remove}: ${product.title}`)}">${this.img(product.image, '56px', 'byb__slot-img')}</button>${label}</li>`;
    }).join('');
    return `<div class="byb__offer"><div class="byb__offer-row"><ol class="byb__slots" role="list">${slots}</ol>`
      + `<p class="byb__offer-count">${esc(fill(this.T.accessoriesAdded, { n: this.accIds().length, total: this.cap }))}</p></div></div>`;
  }

  // Strap, scarf and keyring: "Optional." and the offer line under the heading, then the slots, then (while the
  // discount is live) that the offer counts any strap, scarf or keyring on the site, then the step's list
  accessoryStep(step) {
    const style = this.chosenStyle();
    const helper = `<p class="byb__helper">${esc(this.T.optional)} <span id="byb-offer-line">${esc(this.offerLine())}</span></p>`;
    const note = step === 'strap' && style?.strapNote ? `<p class="byb__helper">${esc(style.strapNote)}</p>` : '';
    const sitewide = this.cfg.discountLive ? `<p class="byb__helper">${esc(this.T.offerSitewide)}</p>` : '';
    const options = this.lists[ACC_STEPS[step]].map((id) => this.accessoryOption(id, step)).join('');
    return this.fieldset(this.T[step], '', `${helper}${note}${this.offer()}${sitewide}<div class="byb__grid2">${options}</div>`, true);
  }

  step_strap() { return this.accessoryStep('strap'); }
  step_scarf() { return this.accessoryStep('scarf'); }
  step_keyring() { return this.accessoryStep('keyring'); }

  chips(name, label, values) {
    const chips = values.map((value) => this.option({ name, value, checked: this.S.pz[name] === value, cls: 'byb__opt--chip', content: esc(value) })).join('');
    return `<fieldset class="byb__sub-step"><legend class="byb__label">${esc(label)}</legend><div class="byb__chips">${chips}</div></fieldset>`;
  }

  step_initials() {
    const pz = this.bag()?.pz || { bag: true, tag: false, gold: true, xl: true };
    const T = this.T;
    let body = '';
    if (pz.bag && pz.tag) body += this.chips('placement', T.placementLabel, ['Bag', 'Tag']);
    body += `<div class="byb__pz-slot" data-pz-slot>${this.bag() ? this.preview(this.bag(), this.S.pz.initials) : ''}</div>`;
    body += this.chips('font', T.fontLabel, ['Classic', 'Script', ...(pz.xl ? ['Classic XL'] : [])]);
    body += `<div class="byb__sub-step"><label class="byb__label" for="byb-initials">${esc(T.initialsLabel)}</label>`
      + `<input class="byb__input" type="text" id="byb-initials" name="initials" value="${esc(this.S.pz.initials)}" maxlength="${this.maxInitials()}" autocomplete="off" autocapitalize="characters" spellcheck="false" aria-describedby="byb-initials-error">`
      + '<p class="byb__error" id="byb-initials-error" data-initials-error></p></div>';
    body += this.chips('emboss', T.embossLabel, [...(pz.gold ? ['Gold'] : []), 'Blind']);
    return this.fieldset(T.initials, esc(T.initialsHelper), body, true);
  }

  step_review() {
    const T = this.T;
    const bag = this.bag();
    const variant = this.variant();
    const totals = this.totals();
    const P = this.S.pz;
    const link = (attr, label, name) => `<button type="button" class="byb__change" ${attr} aria-label="${esc(`${label}: ${name}`)}">${esc(label)}</button>`;
    const item = (image, name, price, extra, cls = '', role = '') => `<li class="byb__line${cls}">${image}<div class="byb__line-body">${role ? `<span class="byb__line-role">${role}</span>` : ''}<span class="byb__line-name">${name}</span>${extra}</div><span class="byb__line-price">${price}</span></li>`;
    const sum = (name, price, cls) => `<li class="byb__line byb__line--sum ${cls}"><span class="byb__line-name">${name}</span><span class="byb__line-price">${price}</span></li>`;

    let lines = item(this.img(bag.image, '64px', 'byb__line-img'), esc(bag.title) + (bag.variants.length > 1 ? `, ${esc(variant.title)}` : ''),
      esc(this.format(totals.bagPrice)), this.preorder(bag) + link('data-go="colour"', T.change, bag.title));
    totals.accessories.forEach(({ id, price }) => {
      const product = this.D.accessories[id];
      const available = this.accVariant(id)?.available;
      lines += item(this.img(product.image, '64px', 'byb__line-img'), esc(product.title), esc(this.format(price)),
        (available ? '' : this.soldOut(id)) + link(`data-remove="${id}"`, T.remove, product.title), ' byb__line--acc', esc(this.roleLabels[this.roleOf[id]]));
    });
    // The offer under the accessories, as a row of its own, so the rows add up to the total. Only when totals() takes
    // the saving off (two or three accessories, discount live, cart in the shop's currency). The empty first cell keeps
    // the label in line with the names above without the height of a photo.
    if (totals.saving > 0) lines += item('<span></span>', esc(T.saving), esc(`\u2212${this.format(totals.saving)}`), '', ' byb__line--offer');
    if (P.initials) {
      const where = this.placement();
      const text = fill(T.bagCaption, { initials: P.initials, font: P.font, embossing: P.emboss }) + (where ? `. ${T.placementLabel}: ${where}` : '');
      const preview = this.preview(bag, P.initials);
      lines += item('<span class="byb__line-img byb__line-img--none"></span>', esc(text), esc(T.free),
        (preview ? `<div class="byb__line-preview">${preview}</div>` : '') + link('data-go="initials"', T.change, T.initials), ' byb__line--pz');
    }
    lines += sum(esc(T.total), esc(this.format(totals.total)), 'byb__line--total');

    let notes = '';
    if (!this.cfg.discountLive && totals.accessories.length) notes += `<p class="byb__notice">${esc(T.discountNotLive)}</p>`;
    else if (this.cfg.discountLive && !totals.eligible) notes += `<p class="byb__notice">${esc(T.nonGbp)}</p>`;
    // The made-to-order line sits under the buttons (renderHead)
    if (T.reassurance) notes += `<p class="byb__reassure">${esc(T.reassurance)}</p>`;
    const share = this.cfg.showShare ? `<button type="button" class="byb__share" data-share>${esc(T.share)}</button>` : '';
    return this.fieldset(T.reviewSubheading, '', `<ul class="byb__lines" role="list">${lines}</ul>${notes}${share}`);
  }

  /* ---------- the image ---------- */

  async renderStage() {
    const token = ++this.stageToken;
    const style = this.chosenStyle();
    if (!style) return;
    const bag = this.bag() || this.heroFor(style);
    if (!bag) return;
    const layers = this.resolveLayers(style, bag);
    let mode = 'look';
    if (layers) {
      const ok = await this.checkLayers(layers.list.map(([, url]) => url));
      if (token !== this.stageToken) return;
      mode = ok ? 'layered' : 'look';
    }
    this.dataset.mode = mode;
    this.els.layers.parentElement.dataset.mode = mode;
    this.els.layers.hidden = mode !== 'layered';
    this.els.look.hidden = mode === 'layered';
    if (mode === 'layered') this.paintLayers(layers);
    else this.paintLook(bag, token);
    this.syncGallery();
  }

  loadLayers() {
    const url = this.cfg.layersUrl;
    this.layerBase = url.split('?')[0].replace(/[^/]*$/, '');
    fetch(url).then((r) => (r.ok ? r.json() : null)).then((map) => {
      this.layerMap = map;
      this.renderStage();
    }).catch(() => log('No layer map at', url)).finally(() => DEBUG && this.debugReport());
  }

  resolveLayers(style, bag) {
    const map = this.layerMap;
    if (!map?.styles) return null;
    const L = TEST ? map.styles._test : map.styles[style.key];
    if (!L) return null;
    const bagLayers = TEST ? (bag.id === style.colours[0] ? L.bags?.['*'] : null) : L.bags?.[bag.handle];
    const report = { style: style.key, bag: bag.handle, bagLayers: Boolean(bagLayers) };
    if (!bagLayers?.bag) { log('Layers: none for this bag, look mode', report); return null; }

    const accLayers = (id) => (TEST ? L.accessories?.[this.roleOf[id]] : L.accessories?.[this.D.accessories[id].handle]);
    const url = (file) => this.layerBase + (TEST_404 && file.includes('charm') ? 'byb-test-missing.svg' : file);
    const back = [];
    const { strap: straps, scarf, charm } = this.S.acc;
    // A bag wears one strap; a build with two shows the photos instead
    if (straps.length > 1) { log('Layers: more than one strap, look mode', report); return null; }
    let strap = bagLayers.default_strap || {};
    if (straps.length) {
      strap = accLayers(straps[0]);
      if (!strap?.front && !strap?.back) { log('Layers: none for the strap, look mode', report); return null; }
    }
    if (strap.back) back.push(['strap-back', strap.back]);
    const extras = [...scarf, ...charm];
    for (const id of extras) {
      const layer = accLayers(id);
      if (!layer?.front) { log(`Layers: none for ${this.D.accessories[id].handle}, look mode`, report); return null; }
      if (layer.back) back.push([`${id}-back`, layer.back]);
    }
    const list = [
      ...(L.shadow ? [['shadow', L.shadow]] : []),
      ...back,
      ['bag', bagLayers.bag],
      ...(strap.front ? [['strap-front', strap.front]] : []),
      ...extras.map((id) => [`${id}`, accLayers(id).front])
    ].map(([key, file]) => [key, url(file)]);
    log('Layers resolved', report, list.map(([key]) => key));
    return { list, canvas: map.canvas || { width: 1600, height: 2000 }, initials: L.initials?.bag };
  }

  checkLayers(urls) {
    return Promise.all(urls.map((url) => {
      if (this.layerOk.has(url)) return this.layerOk.get(url);
      const check = new Promise((resolve) => {
        const image = new Image();
        image.onload = () => resolve(true);
        image.onerror = () => {
          console.warn('[byb] Layer failed to load, showing the product photo instead:', url);
          resolve(false);
        };
        image.src = url;
      });
      this.layerOk.set(url, check);
      return check;
    })).then((results) => results.every(Boolean));
  }

  // Crossfades one image or layer once the new one has decoded; with reduced motion it swaps at once
  fade(next, old) {
    old?.classList.add('is-leaving');
    const swap = () => requestAnimationFrame(() => {
      const motion = !reduced.matches;
      [next, old].forEach((el) => el && (el.style.transition = motion ? `opacity 160ms ${EASE}` : 'none'));
      next?.classList.add('is-on');
      old?.classList.remove('is-on');
      setTimeout(() => old?.remove(), motion ? 180 : 0);
    });
    (next?.decode ? next.decode() : Promise.resolve()).catch(() => {}).then(swap);
  }

  // Photo 1 (the featured image, crossfaded when the bag changes) at once; the bag's other photos once galleryFor has
  // them. The then runs on every paint, so a response that lands after a later render still builds the slides, and
  // the token drops one for a bag that is no longer shown.
  paintLook(bag, token) {
    const look = this.els.look;
    if (look.dataset.pid !== String(bag.id) && bag.image) {
      const first = this.firstSlide();
      const old = first.querySelector('.byb__img');
      const next = new Image();
      next.className = 'byb__img';
      next.sizes = STAGE_SIZES;
      next.srcset = srcset(bag.image);
      next.src = bag.image.src[800];
      next.alt = bag.image.alt;
      first.append(next);
      look.dataset.pid = bag.id;
      this.fade(next, old);
      this.setSlides(bag, [bag.image]);
    }
    if (bag.image) {
      this.galleryFor(bag).then((images) => {
        if (token === this.stageToken && look.dataset.pid === String(bag.id)) this.setSlides(bag, images);
      });
    }
    const tiles = this.accIds().slice(0, 3).map((id) => {
      const product = this.D.accessories[id];
      return `<li class="byb__acc-tile">${this.img(product.image, '72px', 'byb__acc-img')}<span class="byb__acc-name">${esc(this.roleLabels[this.roleOf[id]])}</span></li>`;
    }).join('');
    if (this.els.tiles.innerHTML !== tiles) this.els.tiles.innerHTML = tiles;
  }

  /* ---------- the stage gallery ---------- */

  strip() { return STRIP_STEPS.has(this.step) && stripMedia.matches; }

  newSlide() {
    const slide = document.createElement('div');
    slide.className = 'byb__slide';
    slide.setAttribute('role', 'group');
    return slide;
  }

  firstSlide() {
    let slide = this.els.look.querySelector('.byb__slide');
    if (!slide) { slide = this.newSlide(); this.els.look.prepend(slide); }
    return slide;
  }

  // The bag's photos for the stage, cached per product: the featured image first (already shown), then the rest of
  // its images in admin order from /products/{handle}.js, skipping the personalisation close-ups (alt &INITIALS) and
  // the featured image itself, at most GALLERY_MAX in all. Any failure leaves the featured image alone.
  galleryFor(bag) {
    if (!this.galleryCache.has(bag.id)) {
      const featured = bag.image;
      const own = fileName(featured.src[400]);
      const request = fetch(`${window.Shopify?.routes?.root || '/'}products/${bag.handle}.js`)
        .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`http_${response.status}`))))
        .then((live) => {
          const extra = (live.media || [])
            .filter((media) => media.media_type === 'image' && media.src && !String(media.alt || '').includes('&INITIALS') && fileName(media.src) !== own)
            .map((media) => this.liveImage(media, bag));
          return [featured, ...extra].slice(0, GALLERY_MAX);
        })
        .catch((error) => { log('Gallery: featured image only for', bag.handle, error); return [featured]; });
      this.galleryCache.set(bag.id, request);
    }
    return this.galleryCache.get(bag.id);
  }

  // One image from /products/{handle}.js in the island's shape, the srcset from Shopify's width parameter
  liveImage(media, bag) {
    const src = {};
    for (const w of WIDTHS) {
      const url = new URL(media.src, location.href);
      url.searchParams.set('width', w);
      src[w] = url.toString();
    }
    return { alt: media.alt || bag.title, w: media.width, h: media.height, src };
  }

  // Slide 1 keeps the image it shows (or is fading to); the other slides are rebuilt and the gallery returns to
  // photo 1 with no slide animation. Skipped when the row already holds this bag's photos.
  setSlides(bag, images) {
    const G = this.gallery;
    if (G.pid === bag.id && G.count === images.length) return;
    const look = this.els.look;
    look.querySelectorAll('.byb__slide:not(:first-child)').forEach((slide) => slide.remove());
    this.firstSlide();
    images.slice(1).forEach((image, i) => {
      const slide = this.newSlide();
      const img = new Image();
      img.className = 'byb__img is-on';
      img.sizes = STAGE_SIZES;
      img.srcset = srcset(image);
      img.src = image.src[800];
      img.alt = image.alt;
      img.decoding = 'async';
      if (i > 0) img.loading = 'lazy';
      slide.append(img);
      look.append(slide);
    });
    this.gallery = { pid: bag.id, count: images.length, index: 0 };
    look.scrollTo({ left: 0, behavior: 'auto' });
    this.syncGallery();
  }

  // The counter, the buttons, the keyboard focus and the slide labels, from the gallery state. One photo, the compact
  // strip and layered mode show none of the controls.
  syncGallery() {
    const { count, index } = this.gallery;
    const many = count > 1 && !this.strip() && this.dataset.mode !== 'layered';
    const { look, galleryCount: counter, galleryPrev: prev, galleryNext: next } = this.els;
    counter.textContent = `${index + 1} / ${count}`;
    counter.hidden = !many;
    prev.hidden = next.hidden = !many;
    prev.disabled = index === 0;
    next.disabled = index >= count - 1;
    if (many) look.tabIndex = 0;
    else look.removeAttribute('tabindex');
    look.querySelectorAll('.byb__slide').forEach((slide, i) => slide.setAttribute('aria-label', fill(this.T.gallerySlide, { n: i + 1, total: count })));
  }

  // Which photo is in view, from the scroll position; the first move past photo 1 on a bag is the swipe event
  onGalleryScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      const look = this.els.look;
      const G = this.gallery;
      const index = Math.max(0, Math.min(G.count - 1, Math.round(look.scrollLeft / Math.max(1, look.clientWidth))));
      if (index === G.index) return;
      G.index = index;
      if (index > 0 && !this.swiped.has(G.pid)) {
        this.swiped.add(G.pid);
        publish('byb_gallery_swiped', { product_id: G.pid });
      }
      this.syncGallery();
    });
  }

  onGalleryKey(event) {
    if (this.gallery.count < 2 || this.strip()) return;
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    this.galleryTo(this.gallery.index + step);
  }

  // Scroll to a photo; smooth for a button or key, instant when asked for or with reduced motion. An instant move
  // sets the index itself: on the strip the other slides are gone, so there may be no scroll event to read it from.
  galleryTo(index, instant) {
    const look = this.els.look;
    const i = Math.max(0, Math.min(this.gallery.count - 1, index));
    if (instant) this.gallery.index = i;
    look.scrollTo({ left: i * look.clientWidth, behavior: instant || reduced.matches ? 'auto' : 'smooth' });
  }

  paintLayers({ list, canvas, initials }) {
    const box = this.els.layers;
    box.style.setProperty('--byb-ratio', `${canvas.width} / ${canvas.height}`);
    this.els.tiles.innerHTML = '';
    const keep = new Set(list.map(([key]) => key));
    box.querySelectorAll('img[data-layer]:not(.is-leaving)').forEach((img) => {
      if (!keep.has(img.dataset.layer)) this.fade(null, img);
    });
    // Appending in stack order keeps the z-order; only a layer whose file changed is replaced
    list.forEach(([key, url]) => {
      const current = box.querySelector(`img[data-layer="${key}"]:not(.is-leaving)`);
      if (current && current.getAttribute('src') === url) { box.append(current); return; }
      const next = new Image();
      next.dataset.layer = key;
      next.className = 'byb__layer';
      next.alt = '';
      next.src = url;
      box.append(next);
      this.fade(next, current);
    });
    const P = this.S.pz;
    const text = box.querySelector('.byb__layer-initials') || Object.assign(document.createElement('p'), { className: 'byb__layer-initials initials-preview' });
    const show = initials && P.initials && this.placement() === 'Bag';
    text.hidden = !show;
    if (show) {
      text.textContent = P.initials;
      text.className = `byb__layer-initials initials-preview ${FONT_CLASS[P.font] || 'classic'}${P.emboss === 'Blind' ? ' blind' : ''}`;
      text.style.cssText = `--x:${initials.x * 100}%;--y:${initials.y * 100}%;--size:${initials.size * 100}cqw;--rotate:${initials.rotate || 0}deg`;
    }
    box.append(text);
  }

  placement() {
    const pz = this.bag()?.pz;
    if (!pz) return '';
    return pz.bag && pz.tag ? this.S.pz.placement : pz.bag ? 'Bag' : pz.tag ? 'Tag' : '';
  }

  // The product page's personalisation preview: the tag image, or the bag close-up or crop, with the initials in the
  // chosen font and embossing. Markup, classes and maths as in product-customizer.liquid, styled by its CSS.
  preview(bag, text) {
    const P = this.S.pz;
    const cls = `initials-preview ${FONT_CLASS[P.font] || 'classic'}${P.emboss === 'Blind' ? ' blind' : ''}`;
    const initials = esc(text || 'ABC');
    const placement = this.placement();
    if (placement === 'Tag' && bag.pz.tagImage) {
      const image = bag.pz.tagImage;
      return `<div class="personalisation-preview keyring-holder"><img src="${image.src}" width="${image.w}" height="${image.h}" alt=""><p class="keyring-initials ${cls}" data-initials-preview>${initials}</p></div>`;
    }
    const data = bag.pz.bagPreview;
    const entry = placement === 'Bag' && data && (data.variants[this.variant()?.id] || Object.values(data.variants)[0]);
    if (!entry?.src) return '';
    let style = `--bag-zoom:${data.zoom};--bag-text-x:${entry.text[0]}%;--bag-text-y:${entry.text[1]}%;`;
    if (entry.closeup) {
      style += `--bag-object-position:${entry.focal[0]}% ${entry.focal[1]}%;`;
    } else {
      // Zoom narrows the crop around its centre; the region then covers the 2:0.8 frame without gaps
      const [x, y, w, h] = data.crop;
      const zoom = Math.max(data.zoom || 1, 1);
      const frame = 0.8 / 2;
      const scale = Math.max(zoom / ((w / 100) * entry.w), (frame * zoom) / ((h / 100) * entry.h));
      const width = entry.w * scale;
      const height = entry.h * scale;
      const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
      const left = clamp(0.5 - ((x + w / 2) / 100) * entry.w * scale, 1 - width, 0);
      const top = clamp(frame / 2 - ((y + h / 2) / 100) * entry.h * scale, frame - height, 0);
      style += `--bag-crop-width:${width * 100}%;--bag-crop-x:${(left / width) * 100}%;--bag-crop-y:${(top / height) * 100}%;`;
    }
    return `<div class="personalisation-preview bag-preview-holder" data-mode="${entry.closeup ? 'closeup' : 'crop'}" style="${style}"><img class="bag-preview-image" src="${entry.src}" width="${entry.w}" height="${entry.h}" alt=""><p class="bag-preview-initials ${cls}" data-initials-preview>${initials}</p></div>`;
  }

  prefetch(style) {
    if (!style || this.prefetched.has(style.key)) return;
    this.prefetched.add(style.key);
    const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 200));
    idle(() => this.styleColours(style).forEach((id) => {
      const image = this.D.bags[id].image;
      if (!image) return;
      const img = new Image();
      img.sizes = STAGE_SIZES;
      img.srcset = srcset(image);
    }));
  }

  /* ---------- celebration ---------- */

  // One burst of confetti from the bag photo when the review step first opens. canvas-confetti
  // (assets/confetti.browser.min.js) is requested only now: never on page load, never with the section setting off (no
  // URL) and never with reduced motion.
  celebrate() {
    const url = this.cfg.confettiUrl;
    if (!url || reduced.matches) return;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => this.burst();
    script.onerror = () => log('Confetti failed to load', url);
    document.head.append(script);
  }

  // On its own fixed, full-viewport canvas that takes no clicks, removed when the burst ends (styled in byb.css)
  burst() {
    if (!window.confetti?.create || reduced.matches) return;
    const canvas = document.createElement('canvas');
    canvas.className = 'byb__confetti';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.append(canvas);
    const fire = window.confetti.create(canvas, { resize: true, disableForReducedMotion: true });
    const colors = (this.cfg.celebrateColours || []).filter((colour) => /^#(?:[0-9a-f]{3}){1,2}$/i.test(colour));
    Promise.resolve(fire({
      particleCount: 90, spread: 70, startVelocity: 35, gravity: 1, ticks: 110, scalar: 0.8, shapes: ['square', 'circle'],
      origin: this.burstOrigin(), disableForReducedMotion: true, ...(colors.length ? { colors } : {})
    })).catch(() => {}).finally(() => canvas.remove());
  }

  // The centre of the bag photo, 45% down from its top; without a photo, the middle of the viewport a third of the way down
  burstOrigin() {
    const box = this.photoBox();
    if (!box) return { x: 0.5, y: 1 / 3 };
    const { clientWidth: width, clientHeight: height } = document.documentElement;
    const clamp = (value) => Math.min(Math.max(value, 0), 1);
    return { x: clamp((box.left + box.width / 2) / width), y: clamp((box.top + box.height * 0.45) / height) };
  }

  // The photo as drawn: the slide in view. Its image fills the slide with object-fit: contain (pinned left on the phone
  // strip), so its box is the slide's; the drawn photo is worked out from its natural size and object-position.
  photoBox() {
    if (this.dataset.mode === 'layered') {
      const rect = this.els.layers.getBoundingClientRect();
      return rect.width && rect.height ? rect : null;
    }
    const slide = this.els.look.querySelectorAll('.byb__slide')[this.gallery.index] || this.els.look;
    const img = slide.querySelector('.byb__img.is-on:not(.is-leaving)') || slide.querySelector('.byb__img');
    const rect = img?.getBoundingClientRect();
    if (!rect?.width || !rect.height) return null;
    if (!img.naturalWidth || !img.naturalHeight) return rect;
    const scale = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
    const width = img.naturalWidth * scale;
    const height = img.naturalHeight * scale;
    const [x, y] = getComputedStyle(img).objectPosition.split(' ').map((v) => (v.endsWith('%') ? parseFloat(v) / 100 : 0.5));
    return { left: rect.left + (rect.width - width) * x, top: rect.top + (rect.height - height) * y, width, height };
  }

  /* ---------- cart ---------- */

  pdpProperties(bag) {
    // Exactly what the product page form sends (snippets/product-customizer.liquid), in its order
    if (!bag.pz.on) return {};
    const P = this.S.pz;
    const properties = {};
    const placement = this.placement();
    if (placement) properties.Placement = placement;
    if (P.font) properties.Font = P.font;
    properties.Initials = P.initials;
    const emboss = P.emboss || (bag.pz.gold ? '' : 'Blind');
    if (emboss) properties.Emboss = emboss;
    return properties;
  }

  async addToCart() {
    if (this.busy) return;
    const bag = this.bag();
    const variant = this.variant();
    const T = this.T;
    if (!variant?.available) { this.go(1); return this.say(T.colourSoldOut); }
    const soldOut = this.accIds().find((id) => !this.accVariant(id)?.available);
    if (soldOut) return this.say(fill(T.accessorySoldOut, { name: this.D.accessories[soldOut].title }));

    const bybId = newId();
    const items = [
      { id: variant.id, quantity: 1, properties: { ...this.pdpProperties(bag), _byb_id: bybId, _byb_style: this.S.style } },
      // _byb_offer marks build accessories for reporting. Kite prices any eligible accessory in the cart when a bag is present, not only these lines.
      ...this.accIds().map((id) => ({ id: this.accVariant(id).id, quantity: 1, properties: { _byb_id: bybId, _byb_role: this.roleOf[id], _byb_offer: 'accessory' } }))
    ];

    this.busy = true;
    this.renderBar();
    let result = null;
    try {
      result = await window.bybAdapter.addItemsToCart(items);
    } catch (error) {
      result = null;
    }

    try {
      if (result?.ok) {
        await window.bybAdapter.refreshCart(result).catch(() => {});
        this.say(T.added);
        const totals = this.totals();
        publish('byb_added_to_cart', {
          byb_id: bybId, style: this.S.style, colour_product_id: bag.id, accessory_product_ids: this.accIds(),
          accessory_count: totals.accessories.length, offer_mode: this.mode, saving_pence: totals.saving,
          personalised: Boolean(this.S.pz.initials), value: totals.total / 100, currency: this.D.cartCurrency, mode: this.dataset.mode || 'look'
        });
        window.bybAdapter.openCartDrawer();
      } else if (result?.status === 422) {
        await this.handleSoldOut(result.json?.description || result.json?.message || '');
      } else {
        publish('byb_add_failed', { reason: result ? `http_${result.status}` : 'network' });
        this.say(T.addFailed);
      }
    } finally {
      this.busy = false;
      this.renderBar();
    }
  }

  async handleSoldOut(description) {
    const text = description.toLowerCase();
    const chosen = [this.S.colour, ...this.accIds()];
    const variantOf = (id) => (this.D.bags[id] ? this.variant() : this.accVariant(id));
    let hit = chosen.find((id) => text.includes(this.product(id).title.toLowerCase()));
    if (!hit) {
      for (const id of chosen) {
        try {
          const live = await (await fetch(`${window.Shopify?.routes?.root || '/'}products/${this.product(id).handle}.js`)).json();
          const v = live.variants.find((x) => x.id === variantOf(id).id);
          if (v && !v.available) { hit = id; break; }
        } catch (error) {
          // Keep looking
        }
      }
    }
    publish('byb_add_failed', { reason: hit ? 'sold_out' : 'http_422' });
    if (!hit) return this.say(this.T.addFailed);

    const variant = variantOf(hit);
    variant.available = false;
    const product = this.product(hit);
    product.available = product.variants.some((v) => v.available);
    if (hit === this.S.colour) {
      this.S.colour = null;
      this.S.variant = null;
      this.go(1);
      this.say(this.T.colourSoldOut);
    } else {
      this.render(false);
      this.say(fill(this.T.accessorySoldOut, { name: product.title }));
    }
  }

  async share() {
    this.writeUrl();
    const link = new URL(location.href);
    [...link.searchParams.keys()].filter((key) => key.startsWith('byb_')).forEach((key) => link.searchParams.delete(key));
    const url = link.toString();
    try {
      if (navigator.share) {
        await navigator.share({ url, title: document.title });
        publish('byb_shared', { method: 'share' });
      } else {
        await navigator.clipboard.writeText(url);
        this.say(this.T.linkCopied);
        publish('byb_shared', { method: 'clipboard' });
      }
    } catch (error) {
      // Share sheet dismissed
    }
  }

  /* ---------- debug ---------- */

  debugReport() {
    const handles = new Set([...Object.values(this.D.bags), ...Object.values(this.D.accessories)].map((p) => p.handle));
    Object.entries(this.layerMap?.styles || {}).forEach(([key, L]) => {
      if (key === '_test') return;
      if (!this.styles.some((s) => s.key === key)) console.warn(`[byb] Layer style "${key}" matches no configured style`);
      [...Object.keys(L.bags || {}), ...Object.keys(L.accessories || {})].forEach((handle) => {
        if (!handles.has(handle)) console.warn(`[byb] Layer key "${handle}" (${key}) matches no configured product`);
      });
    });
    // Each accessory list as it rendered (a configured product that is missing here did not resolve, e.g. a draft)
    for (const [step, role] of Object.entries(ACC_STEPS)) {
      console.info(`[byb] ${step} list (${role}): ${this.lists[role].length} products`);
      console.table(this.lists[role].map((id) => ({
        handle: this.D.accessories[id].handle, price: this.format(this.accPrice(id)), available: Boolean(this.accVariant(id)?.available)
      })));
    }

    // The offer on the cheapest and dearest two and three available accessories any build could take, live or not
    const pool = ROLES.flatMap((role) => this.lists[role].filter((id) => this.accVariant(id)?.available).map((id) => ({ id, role, price: this.accPrice(id) })));
    const extremes = (k) => {
      let low = null;
      let high = null;
      const walk = (start, picked) => {
        if (picked.length === k) {
          const sum = picked.reduce((total, a) => total + a.price, 0);
          if (!low || sum < low.sum) low = { sum, picked };
          if (!high || sum > high.sum) high = { sum, picked };
          return;
        }
        for (let i = start; i < pool.length; i++) {
          if (picked.filter((a) => a.role === pool[i].role).length < this.perStep) walk(i + 1, [...picked, pool[i]]);
        }
      };
      walk(0, []);
      return [['cheapest', low], ['dearest', high]];
    };
    const rows = [2, 3].flatMap((k) => extremes(k).filter(([, build]) => build).map(([which, { sum, picked }]) => {
      const saving = this.offerSaving(picked.map((a) => a.price));
      return {
        build: `${which} ${k}${k > this.cap ? ' (over the cap)' : ''}`, handles: picked.map((a) => this.D.accessories[a.id].handle).join(', '),
        list: this.format(sum), saving: this.format(saving), 'offer total': this.format(sum - saving)
      };
    }));
    console.info(`[byb] Offer (${this.mode}${this.cfg.discountLive ? ', live' : ', not live'}):`);
    console.table(rows);
  }
}

if (!customElements.get('build-your-bag')) customElements.define('build-your-bag', BuildYourBag);
})();
